/**
 * Locale-based feature suggestions — shows contextual hints (not auto-activates)
 * based on the user's UI language.
 *
 * RU/CIS locale → suggest foreign_ru layer and Russian-speaking scoring preset.
 * UA locale → suggest foreign_ua layer.
 * DE locale → suggest foreign (German presence in Alto Adige/Trentino).
 */

import { useTranslation } from 'react-i18next';

export interface LocaleSuggestion {
  layerId: string | null;
  presetId: string | null;
  hintKey: string | null;
}

const SUGGESTIONS: Record<string, LocaleSuggestion> = {
  ru: {
    layerId: 'foreign_ru',
    presetId: null,
    hintKey: 'locale.hint.ru',
  },
  uk: {
    layerId: 'foreign_ua',
    presetId: null,
    hintKey: 'locale.hint.ua',
  },
  de: {
    layerId: 'foreign_total',
    presetId: null,
    hintKey: 'locale.hint.de',
  },
};

const DISMISSED_KEY = 'rm:locale_hint_dismissed_v1';

function getDismissed(): Set<string> {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
}

export function dismissSuggestion(lang: string): void {
  try {
    const dismissed = getDismissed();
    dismissed.add(lang);
    localStorage.setItem(DISMISSED_KEY, JSON.stringify([...dismissed]));
  } catch {
    // storage blocked
  }
}

export function useLocaleSuggestion(): (LocaleSuggestion & { isDismissed: boolean }) | null {
  const { i18n } = useTranslation();
  const lang = i18n.language?.split('-')[0] ?? 'en';
  const suggestion = SUGGESTIONS[lang];
  if (!suggestion) return null;
  const isDismissed = getDismissed().has(lang);
  return { ...suggestion, isDismissed };
}
